import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { SelectColumn } from '../../interfaces/sessions';
import { Typography } from '../../interfaces/typography';
import { useFixedDropdown } from './useFixedDropdown';

export interface MultiSelectOption {
  value: string;
  label: string;
  column: SelectColumn;
}

interface FixedMultiSelectProps {
  options: MultiSelectOption[]
  onChange: (values: MultiSelectOption[]) => void;
  selected: MultiSelectOption[];
  placeholder?: string;
}

export function FixedMultiSelect({ options, selected, onChange, placeholder = 'Select options...' }: FixedMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset component state when options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [JSON.stringify(options)]);

  // Explicit filtering logic
  const filteredOptions = React.useMemo(() => {
    return options.filter(option => {
      if (search === '') return true;
      return option.label.toLowerCase().includes(search.toLowerCase());
    });
  }, [options, search]);

  const {
    isOpen,
    setIsOpen,
    dropdownPosition,
    containerRef,
    triggerRef,
    dropdownRef,
    handleToggle
  } = useFixedDropdown(filteredOptions.length);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      const option = filteredOptions[focusedIndex];
      toggleOption(option);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const values = selected.map((v) => v.value)
  const toggleOption = (option: MultiSelectOption) => {
    // Don't toggle special values
    const optionValue = option.value;
    if (optionValue.includes('_SEARCH_') || optionValue.includes('_SEARCHING_') || optionValue.includes('_NO_RESULTS_')) {
      return;
    }
    const newValue = values.includes(optionValue)
      ? selected.filter(v => v.value !== optionValue)
      : [...selected, option];
    onChange(newValue);
  };

  const handleTriggerClick = () => {
    handleToggle();
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSearchChange = (newSearch: string) => setSearch(newSearch);

  return (
    <>
      <div className="relative w-full" ref={containerRef}>
        <div
          ref={triggerRef}
          className="border rounded-lg bg-white px-2 py-1.5 min-h-[38px] cursor-pointer flex gap-1 items-center"
          onClick={handleTriggerClick}
        >
          <div className="flex gap-1 items-center flex-1 min-w-0 overflow-hidden">
            {selected.length > 0 ? (
              <Typography variant="mediumParagraph" color="#1d4ed8" fontWeight={500}>
                {selected.length} selected
              </Typography>
            ) : (
              <Typography variant="mediumParagraph" color="#6B7280" className="truncate">
                {placeholder}
              </Typography>
            )}
          </div>
          <ChevronDown size={18} className="shrink-0 text-gray-400 ml-auto" />
        </div>
      </div>

      {/* Fixed positioned dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed bg-white border rounded-lg shadow-lg flex flex-col"
          style={{
            top: dropdownPosition.top + 'px',
            left: dropdownPosition.left + 'px',
            width: dropdownPosition.width + 'px',
            maxHeight: dropdownPosition.maxHeight + 'px',
            zIndex: 2147483647,
            position: 'fixed',
            isolation: 'isolate'
          }}
        >
          {/* Search header - fixed height */}
          <div className="p-1.5 border-b flex-shrink-0">
            <div className="relative">
              <Search size={18} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-7 pr-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search options..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
                }}
              />
            </div>
          </div>
          
          {/* Options list - scrollable */}
          <div className="flex-1 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-center">
                <Typography variant="mediumParagraph" color="#6B7280">
                  No options found
                </Typography>
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                return (
                  <div
                    key={`${option.value}-${index}`}
                    className={`px-2 py-1.5 cursor-pointer flex items-center gap-2 ${
                      focusedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleOption(option)}
                  >
                    <div className={`shrink-0 w-4 h-4 border rounded flex items-center justify-center ${
                      values.includes(option.value) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {values.includes(option.value) && <Check size={12} className="text-white" />}
                    </div>
                    <Typography variant="mediumParagraph" color="#111827" className="truncate">
                      {option.label}
                    </Typography>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
} 